/**
 * MoneyInput — currency-aware text input (uses SPACE as thousands separator)
 *
 * Behaviour:
 *   • Focus  → shows raw number for easy cursor placement ("1000000" or "1234.56")
 *   • Typing → live-formats with SPACE separator for BOTH VND and USDT
 *              VND: "1 000 000" (integer only)
 *              USDT: "1 234.5678" (space thousands, dot decimal)
 *   • Blur   → re-formats cleanly
 *   • value / onChange always carry the RAW numeric value (number | '')
 *
 * Props:
 *   value    : number | '' | undefined
 *   onChange : (rawValue: number | '') => void
 *   currency : 'VND' | 'USDT'  (default 'VND')
 *   All other props forwarded to <input>
 */
import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';

// ── Formatters — use narrow non-breaking space (U+202F) as thousands sep ──────

/** Format integer-only amount with space separator */
function _fmtInt(n) {
  if (!isFinite(n) || isNaN(n)) return '';
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
}

/** Format USDT: space thousands sep, dot decimal, up to 8 decimal places */
function _fmtUsdt(n) {
  if (!isFinite(n) || isNaN(n)) return '';
  const parts = n.toFixed(8).replace(/\.?0+$/, '').split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
  return parts[1] ? `${intPart}.${parts[1]}` : intPart;
}

/** Live-format an integer string with space separator (while user types VND) */
function _liveVND(digits) {
  if (!digits) return '';
  const n = parseInt(digits, 10);
  return isNaN(n) ? '' : _fmtInt(n);
}

/** Live-format a USDT string being typed — preserves in-progress decimal */
function _liveUSDT(raw) {
  if (!raw) return '';
  // Keep trailing dot or trailing zeros after dot (user is typing)
  const endsWithDot = raw.endsWith('.');
  const parts = raw.split('.');
  const intDigits = parts[0].replace(/[^\d]/g, '');
  const decPart = parts.length > 1 ? parts.slice(1).join('') : null;

  const intFormatted = intDigits
    ? intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')
    : '';

  if (decPart !== null || endsWithDot) {
    return `${intFormatted}.${decPart ?? ''}`;
  }
  return intFormatted;
}

// ── Parse raw numeric value from formatted/typed string ──────────────────────

function parseRaw(inputStr, currency) {
  // Strip ALL whitespace / non-breaking spaces first
  const s = String(inputStr || '').trim().replace(/[\s\u00A0\u202F]/g, '');
  if (!s) return '';

  if (currency === 'VND') {
    const digits = s.replace(/[^\d]/g, '');
    if (!digits) return '';
    const n = parseInt(digits, 10);
    return isNaN(n) ? '' : n;
  }

  // USDT: only digits and single dot allowed
  const clean = s.replace(/[^\d.]/g, '');
  const dotParts = clean.split('.');
  const safe = dotParts.length > 2 ? dotParts[0] + '.' + dotParts.slice(1).join('') : clean;
  if (!safe || safe === '.') return '';
  const n = parseFloat(safe);
  return isNaN(n) ? '' : n;
}

// ── Component ─────────────────────────────────────────────────────────────────

const MoneyInput = React.forwardRef(({
  value,
  onChange,
  currency = 'VND',
  className,
  placeholder,
  disabled,
  id,
  ...rest
}, ref) => {
  const isVND = currency === 'VND';
  const focusedRef = useRef(false);

  const formatBlur = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return '';
    const n = Number(raw);
    if (isNaN(n)) return '';
    return isVND ? _fmtInt(n) : _fmtUsdt(n);
  };

  const [display, setDisplay] = useState(() => formatBlur(value));

  // Sync when external value changes (e.g. form reset, currency switch)
  useEffect(() => {
    if (!focusedRef.current) {
      setDisplay(formatBlur(value));
    }
  }, [value, currency]); // eslint-disable-line

  const handleFocus = (e) => {
    focusedRef.current = true;
    // Show raw digits for easy cursor placement
    const raw = value !== '' && value !== undefined && value !== null ? String(value) : '';
    setDisplay(raw);
    setTimeout(() => e.target.select(), 0);
  };

  const handleChange = (e) => {
    const typed = e.target.value;

    if (isVND) {
      // Strip spaces, keep only digits, then live-format with spaces
      const digits = typed.replace(/[\s\u00A0\u202F]/g, '').replace(/[^\d]/g, '');
      const formatted = _liveVND(digits);
      setDisplay(formatted);
      const raw = digits ? parseInt(digits, 10) : '';
      onChange(isNaN(raw) ? '' : raw);
    } else {
      // USDT: strip spaces, keep digits + single dot, then live-format
      const noSpace = typed.replace(/[\s\u00A0\u202F]/g, '');
      const clean = noSpace.replace(/[^\d.]/g, '');
      // Prevent double dot
      const dotParts = clean.split('.');
      const safe = dotParts.length > 2 ? dotParts[0] + '.' + dotParts.slice(1).join('') : clean;
      setDisplay(_liveUSDT(safe));
      const raw = parseRaw(safe, 'USDT');
      onChange(raw);
    }
  };

  const handleBlur = () => {
    focusedRef.current = false;
    const raw = parseRaw(display, currency);
    onChange(raw);
    setDisplay(formatBlur(raw));
  };

  const defaultPlaceholder = placeholder ?? (isVND ? '1 000 000' : '100.00');

  return (
    <input
      ref={ref}
      id={id}
      type="text"
      inputMode={isVND ? 'numeric' : 'decimal'}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={defaultPlaceholder}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
        'placeholder:text-muted-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...rest}
    />
  );
});

MoneyInput.displayName = 'MoneyInput';
export default MoneyInput;
